import React, { useState, useEffect, useCallback, memo } from 'react';
import { SlidersHorizontal, ChevronDown, ChevronRight, X } from 'lucide-react';
import type { WidgetProps } from './types';
import type { FilterFacetConfig, Facets, FacetBucket } from '../../utils/types';
import { getPublicFilterFacets, getCatalogFacets } from '../../utils/api';
import { PriceRangeFilter } from '../PriceRangeFilter';
import { WidgetPortal } from '../WidgetPortal';
import { useFontSizes } from '../../contexts/FontSizeContext';
import { useI18n } from '../../i18n/I18nContext';
import { translateFacetLabel } from '../../i18n/index';

/** Maps facet field → i18n translation key for the display name */
const FIELD_TO_I18N_KEY: Record<string, string> = {
  category: 'filter.category',
  subcategory: 'filter.subcategory',
  type: 'filter.wineType',
  country: 'filter.country',
  region: 'filter.region',
  grape: 'filter.grape',
  producer: 'filter.producer',
  vintage: 'filter.vintage',
  tags: 'filter.tags',
  body: 'filter.body',
  acidity: 'filter.acidity',
  tannins: 'filter.tannins',
  fruitiness: 'filter.fruitiness',
  price: 'filter.price',
  inStock: 'filter.inStock',
};

/** Maps FilterFacetConfig.field → Facets response key (dynamic, pluralized) */
const FIELD_TO_FACET_KEY: Record<string, string> = {
  category: 'categories',
  subcategory: 'subcategories',
  type: 'wineTypes',
  country: 'countries',
  region: 'regions',
  grape: 'grapes',
  producer: 'producers',
  vintage: 'vintages',
  tags: 'tags',
  body: 'bodies',
  acidity: 'acidities',
  tannins: 'tannins',
  fruitiness: 'fruitiness',
};

// ---------------------------------------------------------------------------
// FacetSection — mirrors the FacetSidebar's FacetSection exactly
// ---------------------------------------------------------------------------

interface FacetSectionProps {
  title: string;
  buckets: FacetBucket[];
  onSelect: (value: string) => void;
  defaultExpanded?: boolean;
  maxVisible?: number;
  bodyClass: string;
  smallClass: string;
}

const FacetSection = memo<FacetSectionProps>(
  ({ title, buckets, onSelect, defaultExpanded = true, maxVisible = 5, bodyClass, smallClass }) => {
    const { t } = useI18n();
    const [isExpanded, setIsExpanded] = useState(defaultExpanded);
    const [showAll, setShowAll] = useState(false);

    const visibleBuckets = showAll ? buckets : buckets.slice(0, maxVisible);
    const hasMore = buckets.length > maxVisible;

    if (buckets.length === 0) return null;

    return (
      <div className="border-b border-gray-100 last:border-b-0">
        <button
          type="button"
          onClick={() => setIsExpanded(!isExpanded)}
          className="w-full flex items-center justify-between py-2.5 px-3 hover:bg-gray-50 transition-colors text-left"
        >
          <span className={`${bodyClass} font-medium text-gray-700`}>{title}</span>
          {isExpanded ? (
            <ChevronDown className="h-3.5 w-3.5 text-gray-400" />
          ) : (
            <ChevronRight className="h-3.5 w-3.5 text-gray-400" />
          )}
        </button>

        {isExpanded && (
          <div className="px-3 pb-2.5 space-y-1">
            {visibleBuckets.map((bucket) => (
              <button
                key={bucket.value}
                type="button"
                onClick={() => onSelect(bucket.value)}
                className={`w-full flex items-center justify-between py-1 px-2 rounded text-left transition-colors ${bodyClass} hover:bg-gray-50 text-gray-600`}
              >
                <span className="truncate pr-2">{bucket.label}</span>
                <span className={`${smallClass} flex-shrink-0 text-gray-400`}>
                  {bucket.count}
                </span>
              </button>
            ))}

            {hasMore && (
              <button
                type="button"
                onClick={() => setShowAll(!showAll)}
                className={`${smallClass} text-wine hover:text-wine-dark px-2 py-1`}
              >
                {showAll ? t('filter.showLess') : t('filter.showMore', { count: buckets.length - maxVisible })}
              </button>
            )}
          </div>
        )}
      </div>
    );
  },
);
FacetSection.displayName = 'FacetSection';

// ---------------------------------------------------------------------------
// FiltersWidget
// ---------------------------------------------------------------------------

export const FiltersWidget: React.FC<WidgetProps> = ({ config, onApplyFilters }) => {
  const { heading, body, small } = useFontSizes();
  const { t, lang } = useI18n();
  const [facetConfigs, setFacetConfigs] = useState<FilterFacetConfig[]>([]);
  const [catalogFacets, setCatalogFacets] = useState<Facets | null>(null);
  const [mobileOpen, setMobileOpen] = useState(false);
  // JS-driven breakpoint: render the inline desktop filters vs the mobile
  // button/drawer via conditional RENDERING, because the `.botilia-show-sm-block`
  // CSS hide can be overridden by the host page in the embed (which left the
  // desktop filters showing on mobile next to the floating button).
  const [isMobile, setIsMobile] = useState(
    () => typeof window !== 'undefined' && window.innerWidth < 640,
  );
  useEffect(() => {
    const mq = window.matchMedia('(max-width: 639px)');
    const handler = (e: MediaQueryListEvent | MediaQueryList) => setIsMobile(e.matches);
    handler(mq);
    mq.addEventListener('change', handler as (e: MediaQueryListEvent) => void);
    return () => mq.removeEventListener('change', handler as (e: MediaQueryListEvent) => void);
  }, []);

  useEffect(() => {
    getPublicFilterFacets(lang).then((configs) => {
      setFacetConfigs(configs.filter((c) => c.enabled));
    });
  }, [lang]);

  useEffect(() => {
    getCatalogFacets(lang).then((data) => {
      if (data) setCatalogFacets(data);
    });
  }, [lang]);

  const handleSelect = useCallback(
    (field: string, value: string) => {
      onApplyFilters?.({ [field]: value });
    },
    [onApplyFilters],
  );

  const handlePriceChange = useCallback(
    (min?: number, max?: number) => {
      onApplyFilters?.({ minPrice: min, maxPrice: max });
    },
    [onApplyFilters],
  );

  const handleInStockToggle = useCallback(() => {
    onApplyFilters?.({ inStock: true });
  }, [onApplyFilters]);

  if (facetConfigs.length === 0 || !catalogFacets) return null;

  const priceMin = catalogFacets.priceRange?.min ?? 0;
  const priceMax = catalogFacets.priceRange?.max ?? 1000;

  const renderFacets = () => facetConfigs.map((facet) => {
        const i18nKey = FIELD_TO_I18N_KEY[facet.field];
        const facetLabel = i18nKey ? t(i18nKey as any) : facet.name;

        // --- Price slider ---
        if (facet.field === 'price') {
          return (
            <div key={facet.id} className="border-b border-gray-100 px-3 py-2.5">
              <span className={`${body} font-medium text-gray-700 block mb-2`}>
                {facetLabel}
              </span>
              <PriceRangeFilter
                rangeMin={priceMin}
                rangeMax={priceMax}
                onChange={handlePriceChange}
              />
            </div>
          );
        }

        // --- In-stock toggle ---
        if (facet.field === 'inStock') {
          return (
            <div key={facet.id} className="border-b border-gray-100 px-3 py-2.5">
              <button
                type="button"
                onClick={handleInStockToggle}
                className={`w-full flex items-center justify-between py-1.5 px-2 rounded ${body} transition-colors hover:bg-gray-50 text-gray-600`}
              >
                <span>{t('filter.inStockOnly')}</span>
                <span className="w-4 h-4 rounded border border-gray-300 flex items-center justify-center" />
              </button>
            </div>
          );
        }

        // --- Standard list facets ---
        const facetKey = FIELD_TO_FACET_KEY[facet.field] ?? facet.field;
        const rawBuckets = catalogFacets[facetKey];
        const buckets: FacetBucket[] = Array.isArray(rawBuckets)
          ? rawBuckets.map((b) => ({ ...b, label: translateFacetLabel(lang, b.label) }))
          : [];

        return (
          <FacetSection
            key={facet.id}
            title={facetLabel}
            buckets={buckets}
            onSelect={(value) => handleSelect(facet.field, value)}
            defaultExpanded={facet.expanded}
            maxVisible={5}
            bodyClass={body}
            smallClass={small}
          />
        );
      });

  const sectionTitle = config.title ?? t('section.browse');

  return (
    <>
      {/* Desktop: render inline filters (conditional render, not CSS hide) */}
      {!isMobile && (
        <div className="w-full bg-white border border-gray-100 rounded-lg">
          <div className="flex items-center gap-1.5 px-3 py-2.5 border-b border-gray-100 bg-white">
            <SlidersHorizontal className="h-3.5 w-3.5 text-gray-500" />
            <span className={`${heading} font-medium text-gray-700`}>{sectionTitle}</span>
          </div>
          {renderFacets()}
        </div>
      )}

      {/* Mobile: filter trigger — inline & right-aligned so it sits at the top
          of the discovery content (under the trending-searches header), matching
          the results view's filter button (same top-right placement and size)
          rather than floating at the bottom. The overlay below is portaled out
          of the scroller; this trigger flows with the content and doesn't need to be. */}
      {isMobile && (
        <div className="flex justify-end mb-3 sm:mb-4">
          <button
            type="button"
            onClick={(e) => { e.stopPropagation(); setMobileOpen(true); }}
            className="flex items-center gap-2 px-3.5 py-2.5 bg-[#f02e69] text-white rounded-full shadow-md active:scale-95 transition-transform"
            aria-label={sectionTitle}
          >
            <SlidersHorizontal className="h-[18px] w-[18px]" />
            <span className="text-[13px] font-medium">{sectionTitle}</span>
          </button>
        </div>
      )}

      {/* Mobile fullscreen overlay (portaled so it covers the wine cards
          instead of being painted behind them on iOS) */}
      {isMobile && mobileOpen && (
        <WidgetPortal>
          <div className="fixed inset-0 z-[10003] bg-white flex flex-col animate-fade-in">
            <div className="flex items-center justify-between px-4 py-3 border-b border-gray-200 flex-shrink-0">
              <div className="flex items-center gap-2">
                <SlidersHorizontal className="h-4 w-4 text-gray-500" />
                <h2 className="text-base font-semibold">{sectionTitle}</h2>
              </div>
              <button
                type="button"
                onClick={() => setMobileOpen(false)}
                className="p-2 text-gray-500 hover:text-[#f02e69] rounded-lg transition-colors"
                aria-label={t('search.close')}
              >
                <X className="h-5 w-5" />
              </button>
            </div>
            <div className="flex-1 overflow-y-auto">
              {renderFacets()}
            </div>
            <div className="flex-shrink-0 px-4 py-3 border-t border-gray-200">
              <button
                type="button"
                onClick={() => setMobileOpen(false)}
                className="w-full px-4 py-3 text-sm font-medium text-white bg-[#f02e69] rounded-xl hover:bg-[#d42860] transition-colors"
              >
                {t('filter.applyFilters')}
              </button>
            </div>
          </div>
        </WidgetPortal>
      )}
    </>
  );
};
